import {
  Base,
  ICustomQuestionTypeConfiguration,
  PanelModelBase,
  Question,
  QuestionCompositeModel,
  QuestionCustomModel,
  QuestionPanelDynamicModel,
  settings,
} from "survey-core";

/**
 * Detects the one kind of change inside a dynamic panel that no save handler
 * covers: a composite question's value.
 *
 * WHY THIS EXISTS: survey-core raises `onDynamicPanelValueChanging` from
 * `questionValueChanging`, which walks up exactly one level - it fires only when
 * the changed question's `parentQuestion` is itself the dynamic panel. What the
 * respondent edits inside a composite is an element of the composite's content
 * panel, so its `parentQuestion` is the composite, not the panel, and the event
 * never fires. The change surfaces only as `onValueChanging` on the dynamic
 * panel itself, which survey-page skips on the assumption that the
 * dynamic-panel event covers it. Nothing saved, so the value was gone after a
 * refresh.
 *
 * This went unnoticed until now because crp-non-textile's production-output
 * panels are the first place in this codebase where a composite sits inside a
 * dynamic panel - `crp-pp-global-selector` sits in a static panel in crp-v3,
 * and every other composite is page-level.
 *
 * Plain and matrix children are deliberately excluded: they already save
 * through `onDynamicPanelValueChanging` and `onMatrixCellValueChanging`, and
 * re-saving the whole array on every matrix keystroke would multiply the
 * traffic of a 12-row table by the number of panels.
 *
 * @see onValueChanging in src/pages/survey-page.tsx - the only handler that sees
 * this change
 * @see schemas/crp-non-textile-schema.json - the production-output panels
 */

/** A composite is either an `elementsJSON` or a `questionJSON` custom question. */
const isComposite = (question: Question): boolean =>
  question instanceof QuestionCompositeModel ||
  question instanceof QuestionCustomModel;

/**
 * The data keys of a dynamic panel's composite template elements. The backend
 * injects a distinct valueName per element and keys panel entries by it, so the
 * key is the valueName rather than the element name.
 */
const getCompositeKeys = (question: QuestionPanelDynamicModel): string[] =>
  question.template.questions
    .filter(isComposite)
    .map((child) => child.getValueName());

const toEntries = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value : [];

const entryAt = (
  entries: Record<string, unknown>[],
  index: number
): Record<string, unknown> => entries[index] ?? {};

const differs = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) !== JSON.stringify(b);

/**
 * True when a dynamic panel's new value differs from its old one in a composite
 * child's key, meaning the change needs saving here because no other handler
 * will see it.
 *
 * A shrinking array is a panel removal, which `onDynamicPanelRemoving` and
 * `syncSubTypePanels` handle, so it is never treated as a composite change.
 */
export const isCompositeChildValueChange = (
  question: Base,
  oldValue: unknown,
  newValue: unknown
): boolean => {
  if (question?.getType() !== "paneldynamic") return false;

  const compositeKeys = getCompositeKeys(question as QuestionPanelDynamicModel);
  if (compositeKeys.length === 0) return false;

  const next = toEntries(newValue);
  const previous = toEntries(oldValue);
  if (next.length === 0 || previous.length > next.length) return false;

  return next.some((entry, index) =>
    compositeKeys.some((key) =>
      differs(entry?.[key], entryAt(previous, index)[key])
    )
  );
};

/**
 * The panel that holds a question inside a dynamic panel, walking past any
 * static panels in between so a nested question still reaches all its siblings.
 */
const findPanelItem = (
  question: Question,
  dynamicPanel: Question
): PanelModelBase | undefined => {
  let container = question.parent as PanelModelBase | undefined;
  while (container && container.parentQuestion !== dynamicPanel) {
    container = container.parent as PanelModelBase | undefined;
  }
  return container;
};

/**
 * Re-announces a composite's inner value change to its siblings inside a dynamic
 * panel under the full `panel.<composite>.<inner>` path, so their
 * `setValueExpression` re-evaluates.
 *
 * WHY THIS EXISTS: picking a global unit moved the empty cells of a
 * production-output table but left the ones the respondent had already filled on
 * their old unit.
 *
 * A dynamic panel announces a panel-local change as `panel.<questionName>`
 * (`QuestionPanelDynamicItem.setValue`), and `canExecuteTriggerByKeysCore` only
 * runs a trigger when the announced key matches one of the expression's
 * variables. A composite's value lives one level deeper, so the expression's
 * variable is `panel.<composite>.<inner>` and the announced `panel.<composite>`
 * never matches it - verified by nudging both names: only the full path fires.
 * `defaultValueExpression` still moved the empty cells because it also runs in
 * the survey-wide `runConditions` sweep, which is exactly why empty cells
 * followed the selector and filled ones did not.
 *
 * The page-level shape crp-v3 uses (`{selector.inner}`) is unaffected: the
 * survey announces that change under the composite's own name, which does match.
 *
 * Firing on the composite's own change - rather than on any change in the panel
 * - is what keeps the semantics of `setValueExpression` intact: a cell the
 * respondent deliberately set to something else survives unrelated edits and
 * only moves when the selector itself moves.
 *
 * @see withPanelTriggerRelay - wires this into every registered composite
 */
export const relayCompositeChangeToPanel = (
  question: Question,
  innerName: string,
  newValue: unknown
): void => {
  const dynamicPanel = question?.parentQuestion;
  if (dynamicPanel?.getType() !== "paneldynamic" || !innerName) return;

  const panelItem = findPanelItem(question, dynamicPanel);
  if (!panelItem) return;

  const path = `${settings.expressionVariables.panel}.${question.getValueName()}.${innerName}`;
  panelItem.questions.forEach((sibling) => sibling.runTriggers(path, newValue));
};

/**
 * Wraps a composite's configuration so its inner changes reach the siblings of
 * whatever dynamic panel it is dropped into, keeping the composite's own
 * `onValueChanged` intact.
 *
 * Applied to every composite at registration rather than to the one that needs
 * it today, because the gap belongs to survey-core, not to the selector: any
 * composite placed in a dynamic panel would hit it.
 *
 * @see src/component-collection.ts - where composites are registered
 */
export const withPanelTriggerRelay = <
  T extends ICustomQuestionTypeConfiguration
>(
  config: T
): T => ({
  ...config,
  onValueChanged(question: Question, name: string, newValue: unknown) {
    relayCompositeChangeToPanel(question, name, newValue);
    config.onValueChanged?.(question, name, newValue);
  },
});
